import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { X, Loader2, Sparkles, Wand2, Zap, TimerReset } from "lucide-react";

/**
 * StrategyWhitePaperModal — the input form for the 6-phase SEO
 * white-paper pipeline. Pops open when the admin clicks "Generate
 * SEO White Paper" on the Strategy editor. Auto-populates from:
 *   • The client record (company name, business description, brand)
 *   • The client's business units (websites, service lines)
 *   • The strategy's live_data_snapshot (GSC/AHREFS blocks flattened
 *     to a paste-friendly text summary)
 *
 * On submit, calls `/whitepaper/start` which fires the pipeline in
 * the background and returns immediately. Parent then starts polling
 * the /status endpoint every 4 seconds until status is complete/failed.
 */
export default function StrategyWhitePaperModal({
  clientId, strategy, onClose, onStarted,
}) {
  const [busy, setBusy] = useState(false);
  const [parallel, setParallel] = useState(true);
  const [form, setForm] = useState({
    client_name: "",
    website_url: "",
    business_description: "",
    services: "",
    location: "",
    target_audience: "",
    never_target: "",
    key_clients: "",
    primary_hub_url: "",
    primary_hub_keywords: "",
    secondary_hub_url: "",
    secondary_hub_keywords: "",
    gsc_data: "",
    ahrefs_data: "",
    existing_content_urls: "",
    existing_content_copy: "",
    author_byline: "TBC — use generic team byline",
    campaign_period: "",
    report_date: new Date().toISOString().slice(0, 10),
    agency_name: "Iceberg Digital",
    agency_email: "",
  });

  // Auto-populate from the client + strategy on first mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: c } = await api.get(`/clients/${clientId}`);
        const { data: bus } = await api.get(`/clients/${clientId}/business-units`);
        const primaryBU = (bus?.business_units || [])
          .find((b) => b.is_primary) || (bus?.business_units || [])[0] || {};
        // Flatten live_data_snapshot for GSC + AHREFS
        const snapshot = strategy?.live_data_snapshot?.live_sources || [];
        const gscBlock = snapshot
          .filter((s) => s.platform === "google_search_console" && s.status === "ok")
          .map((s) => {
            const t = s.data?.totals || {};
            const q = (s.data?.breakdowns?.top_queries || []).slice(0, 25);
            return [
              `${s.business_unit_name || s.resource_label || "Overall"} — ${s.resource_id || ""}`,
              `Clicks: ${t.clicks ?? "—"} · Impressions: ${t.impressions ?? "—"} · Avg pos: ${t.position ?? "—"}`,
              q.length ? "Top queries (query | impressions | clicks | position):" : "",
              ...q.map((r) => `${r.query || r.key} | ${r.impressions ?? "—"} | ${r.clicks ?? "—"} | ${r.position ?? "—"}`),
            ].join("\n");
          }).join("\n\n");
        const ahBlock = snapshot
          .filter((s) => s.platform === "ahrefs" && s.status === "ok")
          .map((s) => {
            const t = s.data?.totals || {};
            const k = s.data?.breakdowns?.top_organic_keywords || [];
            const b = s.data?.breakdowns?.top_backlinks || [];
            return [
              `${s.resource_label || s.resource_id}: DR ${t.domain_rating ?? "—"} · Org traffic ${t.org_traffic ?? "—"} · Backlinks ${t.backlinks ?? "—"} · Ref domains ${t.refdomains ?? "—"} · Org keywords ${t.org_keywords ?? "—"}`,
              k.length ? "Top organic keywords (kw | pos | vol | traffic):" : "",
              ...k.map((r) => `${r.keyword} | ${r.position} | ${r.volume} | ${r.traffic}`),
              b.length ? "Sample top backlinks (source | DR | anchor):" : "",
              ...b.slice(0, 5).map((r) => `${r.source_url} | ${r.domain_rating_source} | ${r.anchor}`),
            ].filter(Boolean).join("\n");
          }).join("\n\n");
        if (cancelled) return;
        setForm((f) => ({
          ...f,
          client_name: c?.company_name || f.client_name,
          website_url: primaryBU.website_url || c?.website || "",
          business_description: c?.brief_summary || c?.notes || "",
          location: primaryBU.location || c?.city || c?.state || "",
          services: primaryBU.services || c?.services || "",
          campaign_period: strategy?.period_type
            ? `${strategy.period_type.charAt(0).toUpperCase() + strategy.period_type.slice(1)} — ending ${strategy.period_end}`
            : "",
          gsc_data: gscBlock || "(auto-pull did not return GSC data — paste raw GSC CSV here)",
          ahrefs_data: ahBlock || "(auto-pull did not return AHREFS data — paste raw AHREFS export here)",
          agency_email: c?.agency_owner_email || "",
        }));
      } catch (e) {
        toast.error("Could not auto-populate all fields — fill in manually");
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, strategy?.strategy_id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const start = async () => {
    if (!form.client_name || !form.website_url || !form.primary_hub_url) {
      toast.error("Client name, website URL, and primary hub URL are required.");
      return;
    }
    setBusy(true);
    try {
      await api.post(
        `/clients/${clientId}/strategies/${strategy.strategy_id}/whitepaper/start`,
        { ...form, parallel_phase3: parallel },
      );
      toast.success("Pipeline started. Polling for progress…");
      onStarted?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start pipeline");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm
      flex items-start justify-center overflow-y-auto py-8 px-4"
      data-testid="whitepaper-modal">
      <div className="bg-white w-full max-w-4xl rounded-sm shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4
          border-b border-zinc-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#002FA7]" />
            <div>
              <div className="font-display font-bold text-lg leading-tight">
                Generate SEO White Paper
              </div>
              <div className="text-[11px] text-zinc-500 mt-0.5">
                6-phase Claude pipeline · Planning in Opus · Execution in Sonnet
              </div>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}
            data-testid="whitepaper-modal-close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          <Section title="Client identity" subtitle="Auto-populated from the client record; edit anything that's wrong.">
            <Grid>
              <Field label="Client name *"><Input value={form.client_name}
                onChange={set("client_name")} data-testid="wp-client-name" /></Field>
              <Field label="Website URL *"><Input value={form.website_url}
                onChange={set("website_url")} placeholder="https://..." data-testid="wp-website-url" /></Field>
              <Field label="Location"><Input value={form.location}
                onChange={set("location")} placeholder="Sydney, AU" data-testid="wp-location" /></Field>
              <Field label="Services (comma-separated)"><Input value={form.services}
                onChange={set("services")} data-testid="wp-services" /></Field>
            </Grid>
            <Field label="Business description (2-4 sentences: what they do, how long, staff count, target markets)">
              <Textarea rows={3} value={form.business_description}
                onChange={set("business_description")} data-testid="wp-business" />
            </Field>
          </Section>

          <Section title="Audience targeting" subtitle="Specificity here shapes the entire content plan — be exact.">
            <Field label="Target audience (who buys — be specific)">
              <Textarea rows={2} value={form.target_audience}
                onChange={set("target_audience")}
                placeholder="e.g. FM managers at universities, hospitals, shopping centres"
                data-testid="wp-target-audience" />
            </Field>
            <Field label="Never target (audiences to exclude)">
              <Textarea rows={2} value={form.never_target} onChange={set("never_target")}
                placeholder="e.g. residential homeowners, single-trade small business"
                data-testid="wp-never-target" />
            </Field>
            <Field label="Key clients (named references, or 'none confirmed yet')">
              <Textarea rows={2} value={form.key_clients} onChange={set("key_clients")}
                data-testid="wp-key-clients" />
            </Field>
          </Section>

          <Section title="Hub pages" subtitle="The two priority conversion pages the entire cluster will support.">
            <Grid>
              <Field label="Primary hub URL *"><Input value={form.primary_hub_url}
                onChange={set("primary_hub_url")}
                placeholder="/building-and-facilities-management/"
                data-testid="wp-primary-hub-url" /></Field>
              <Field label="Primary hub keywords (2-3)"><Input value={form.primary_hub_keywords}
                onChange={set("primary_hub_keywords")}
                data-testid="wp-primary-hub-keywords" /></Field>
              <Field label="Secondary hub URL"><Input value={form.secondary_hub_url}
                onChange={set("secondary_hub_url")}
                data-testid="wp-secondary-hub-url" /></Field>
              <Field label="Secondary hub keywords (2-3)"><Input value={form.secondary_hub_keywords}
                onChange={set("secondary_hub_keywords")}
                data-testid="wp-secondary-hub-keywords" /></Field>
            </Grid>
          </Section>

          <Section title="Data feeds"
            subtitle="Auto-pulled from live snapshot where available — edit or replace with raw exports for higher fidelity.">
            <Field label="GSC data (query/page performance — paste CSV or the auto-pulled summary)">
              <Textarea rows={6} value={form.gsc_data} onChange={set("gsc_data")}
                className="font-mono text-[11px]" data-testid="wp-gsc-data" />
            </Field>
            <Field label="AHREFS / SEMrush data (DR, organic traffic, top pages, keyword gaps, backlinks)">
              <Textarea rows={6} value={form.ahrefs_data} onChange={set("ahrefs_data")}
                className="font-mono text-[11px]" data-testid="wp-ahrefs-data" />
            </Field>
            <Field label="Existing content URLs (one per line — used for cannibalization + retrofits)">
              <Textarea rows={4} value={form.existing_content_urls}
                onChange={set("existing_content_urls")}
                className="font-mono text-[11px]" data-testid="wp-existing-urls" />
            </Field>
          </Section>

          <Section title="Publication meta">
            <Grid>
              <Field label="Author byline (reviewer name + credential)"><Input value={form.author_byline}
                onChange={set("author_byline")} data-testid="wp-author-byline" /></Field>
              <Field label="Campaign period"><Input value={form.campaign_period}
                onChange={set("campaign_period")}
                placeholder="Q1 2027 or January–March 2027"
                data-testid="wp-campaign-period" /></Field>
              <Field label="Report date"><Input type="date" value={form.report_date}
                onChange={set("report_date")} data-testid="wp-report-date" /></Field>
              <Field label="Agency name"><Input value={form.agency_name}
                onChange={set("agency_name")} data-testid="wp-agency-name" /></Field>
              <Field label="Agency email"><Input value={form.agency_email}
                onChange={set("agency_email")}
                placeholder="info@icebergdigital.com.au" data-testid="wp-agency-email" /></Field>
            </Grid>
          </Section>

          <Section title="Execution mode"
            subtitle="Phase 3 writes 8 blog posts — you can run them concurrently or sequentially.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button type="button" onClick={() => setParallel(true)}
                data-testid="wp-mode-parallel"
                className={`text-left p-4 rounded-sm border-2 transition-all ${
                  parallel ? "border-[#002FA7] bg-blue-50"
                           : "border-zinc-200 hover:border-zinc-300"}`}>
                <div className="flex items-center gap-2 font-bold text-sm">
                  <Zap className="h-4 w-4" />
                  Parallel · ~2 min
                </div>
                <div className="text-[11px] text-zinc-600 mt-1 leading-relaxed">
                  Fires all 8 Sonnet calls at once. Fastest to finish, higher cost
                  per token burst, one bad prompt fails only its post.
                </div>
              </button>
              <button type="button" onClick={() => setParallel(false)}
                data-testid="wp-mode-sequential"
                className={`text-left p-4 rounded-sm border-2 transition-all ${
                  !parallel ? "border-[#002FA7] bg-blue-50"
                            : "border-zinc-200 hover:border-zinc-300"}`}>
                <div className="flex items-center gap-2 font-bold text-sm">
                  <TimerReset className="h-4 w-4" />
                  Sequential · ~8 min
                </div>
                <div className="text-[11px] text-zinc-600 mt-1 leading-relaxed">
                  One post at a time. Slower but steadier — if the first post
                  reveals a prompt bug, you can cancel before spending on 7 more.
                </div>
              </button>
            </div>
          </Section>

          <div className="border border-amber-200 bg-amber-50 rounded-sm p-3 text-[11px] leading-relaxed">
            <strong>What happens next:</strong> the pipeline runs in the background —
            you can close this window and come back. Progress polls every 4 seconds.
            Final output is a print-ready HTML white paper stored on the strategy
            record; you can preview, download PDF, and share it with the client from
            the editor. Estimated cost per run: <Badge variant="outline" className="ml-1">USD $2–3</Badge>.
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t
          border-zinc-200 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose} disabled={busy}
            data-testid="whitepaper-cancel">Cancel</Button>
          <Button onClick={start} disabled={busy}
            className="bg-[#002FA7] hover:bg-[#001F70] text-white"
            data-testid="whitepaper-start">
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  : <Wand2 className="h-3.5 w-3.5 mr-1.5" />}
            Start pipeline
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div>
      <div className="mb-2">
        <div className="text-[10px] font-mono uppercase tracking-wider text-[#002FA7] font-bold">
          {title}
        </div>
        {subtitle && (
          <div className="text-[11px] text-zinc-500 mt-0.5">{subtitle}</div>
        )}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] font-semibold text-zinc-700">{label}</Label>
      {children}
    </div>
  );
}
